import { CONFIG } from '../config';
import { publicAssetUrl } from './publicUrl';
import {
  extractPropertyImageKey,
  shouldResolvePropertyKeyViaUploadApi,
  staticPathForPropertyKey,
  uploadApiPathForPropertyKey
} from '@merlot/common/propertyImageKeys.js';

/**
 * Fix legacy export URLs (double `property/` segment, localhost build host).
 */
function normalizePropertyUploadUrl(url) {
  let t = String(url || '').trim();
  if (!t) return '';

  if (/^(https?:|\/\/|data:|blob:)/i.test(t)) {
    t = t.replace(/\/v1\/uploads\/property\/property\//i, '/v1/uploads/property/');
    if (/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?\//i.test(t)) {
      const pathMatch = t.match(/\/v1\/uploads\/property\/.+$/i);
      if (pathMatch) {
        const base = CONFIG.API_BASE_URL.replace(/\/v1\/?$/, '');
        return `${base}${pathMatch[0]}`;
      }
    }
    return t;
  }

  if (t.startsWith('/v1/uploads/property/property/')) {
    return t.replace('/v1/uploads/property/property/', '/v1/uploads/property/');
  }

  return t;
}

function absoluteUploadUrlForPropertyKey(propertyKey) {
  const path = uploadApiPathForPropertyKey(propertyKey);
  if (!path) return '';
  const apiBase = CONFIG.API_BASE_URL;
  if (apiBase.startsWith('http')) {
    const origin = apiBase.replace(/\/v1\/?$/, '');
    return `${origin}${path}`;
  }
  return path;
}

/**
 * Resolve a property image key or legacy path for display.
 *
 * Shared upload keys (`property/…`) are locale-independent; serve via `/v1/uploads/…`
 * in dev and when the wire payload already points at uploads. Static `/images/property/…`
 * is used for SSG/production bundles only.
 *
 * @param {unknown} key
 * @param {{ useUploadApi?: boolean }} [options]
 */
export function resolvePropertyImageUrl(key, { useUploadApi = false } = {}) {
  if (key == null || key === '') return '';
  const t = String(key).trim();
  if (!t) return '';

  const propertyKey = extractPropertyImageKey(t);
  if (
    propertyKey &&
    shouldResolvePropertyKeyViaUploadApi(t, {
      useUploadApi,
      dev: import.meta.env.DEV
    })
  ) {
    return absoluteUploadUrlForPropertyKey(propertyKey);
  }

  if (/^(https?:|\/\/|data:|blob:)/i.test(t)) {
    const staticFromUrl = staticPathForPropertyKey(t);
    if (staticFromUrl && !shouldResolvePropertyKeyViaUploadApi(t, { useUploadApi, dev: import.meta.env.DEV })) {
      return staticFromUrl;
    }
    return normalizePropertyUploadUrl(t);
  }

  const staticPath = staticPathForPropertyKey(t);
  if (staticPath) return staticPath;

  return publicAssetUrl(t);
}
